import { useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";

const PATHWAYS = [
  { label: "Model Bookings", fr: "Réservation de modèles", email: "contact.email.models", testid: "contact-pathway-models" },
  { label: "Acting & Casting", fr: "Acteurs et casting", email: "contact.email.actors", testid: "contact-pathway-actors" },
  { label: "Voice-Over Bookings", fr: "Réservation voix", email: "contact.email.voice", testid: "contact-pathway-voice" },
  { label: "Talent Submissions", fr: "Candidatures", email: "contact.email.submissions", testid: "contact-pathway-submissions" },
  { label: "General Business", fr: "Affaires générales", email: "contact.email.general", testid: "contact-pathway-general" },
  { label: "Press & Partnerships", fr: "Presse et partenariats", email: "contact.email.press", testid: "contact-pathway-press" },
];

const inputCls =
  "w-full border border-white/15 bg-black/40 px-4 py-3.5 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-gold";

export default function Contact() {
  const { t, lang } = useLanguage();
  useSEO(t("nav.contact") + " — Diamotims Agency", "Contact Diamotims Agency Montréal — bookings, casting, voice-over, submissions, press and partnerships.");
  const [form, setForm] = useState({ pathway: PATHWAYS[4].label, name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const honeypotRef = useRef(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pathwayLabel = (p) => (lang === "fr" ? p.fr : p.label);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { ...form, website: honeypotRef.current?.value || "" });
      setDone(true);
      setForm({ pathway: PATHWAYS[4].label, name: "", email: "", message: "" });
      toast.success(t("contact.sent"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-28 lg:pt-36">
      <div className="mx-auto max-w-[1800px] px-4 pb-24 sm:px-8 lg:px-16">
        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-gold sm:text-xs">{t("contact.eyebrow")}</p>
        <h1 className="mt-4 font-display text-5xl font-light uppercase leading-[0.95] tracking-tight text-ivory sm:text-7xl lg:text-8xl">
          <MaskedLine delay={0.1}>{t("contact.title1")}</MaskedLine>
          <MaskedLine delay={0.22}>
            <span className="italic text-gold">{t("contact.title2")}</span>
          </MaskedLine>
        </h1>

        <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <FadeUp>
              <div className="grid grid-cols-1 gap-px border border-white/10 bg-white/10 sm:grid-cols-2">
                {PATHWAYS.map((p) => (
                  <a key={p.label} href={`mailto:${t(p.email)}`} data-testid={p.testid} className="group bg-[#0A0A0A] p-6 transition-colors hover:bg-[#121212]">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{pathwayLabel(p)}</div>
                    <div className="mt-2 font-mono text-xs text-white/60 transition-colors group-hover:text-white">{t(p.email)}</div>
                  </a>
                ))}
              </div>
            </FadeUp>
            <FadeUp delay={0.15} className="mt-10 border border-white/10 bg-[#121212] p-8">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{t("footer.headquarters")}</div>
              <div className="mt-4 space-y-1.5 font-mono text-xs leading-relaxed text-white/60">
                <div>{t("footer.city")}</div>
                <div>45.5017° N, 73.5673° W</div>
              </div>
            </FadeUp>
          </div>

          <FadeUp delay={0.2} className="lg:col-span-5">
            {done ? (
              <div data-testid="contact-success-message" className="flex h-full min-h-[380px] flex-col items-center justify-center border border-gold/40 bg-[#121212] p-10 text-center">
                <div className="flex h-14 w-14 items-center justify-center border border-gold text-gold">
                  <Check className="h-6 w-6" />
                </div>
                <h2 className="mt-6 font-display text-2xl font-light uppercase tracking-tight text-white">{t("contact.sent")}</h2>
                <button data-testid="contact-again-button" onClick={() => setDone(false)} className="mt-8 border border-white/30 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.3em] text-white transition-colors hover:border-gold hover:text-gold">
                  {t("contact.again")}
                </button>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="border border-white/10 bg-[#121212] p-6 sm:p-8">
                <input ref={honeypotRef} type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
                <div className="space-y-5">
                  <label className="block">
                    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">{t("contact.topic")}</span>
                    <select data-testid="contact-select-pathway" value={form.pathway} onChange={set("pathway")} className={`${inputCls} appearance-none`}>
                      {PATHWAYS.map((p) => (
                        <option key={p.label} value={p.label} className="bg-[#121212]">{pathwayLabel(p)}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">{t("contact.name")}</span>
                    <input data-testid="contact-input-name" required minLength={2} value={form.name} onChange={set("name")} className={inputCls} />
                  </label>
                  <label className="block">
                    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">{t("contact.email")}</span>
                    <input data-testid="contact-input-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} />
                  </label>
                  <label className="block">
                    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">{t("contact.message")}</span>
                    <textarea data-testid="contact-input-message" required rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} />
                  </label>
                </div>
                <button data-testid="contact-submit-button" type="submit" disabled={busy} className="group mt-7 flex w-full items-center justify-center gap-3 bg-ivory px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] text-black transition-colors duration-300 hover:bg-gold disabled:opacity-50">
                  {busy ? t("contact.sending") : t("contact.send")}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                </button>
              </form>
            )}
          </FadeUp>
        </div>
      </div>
    </main>
  );
}
